import React, { useMemo } from 'react';
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, History, MessageSquare, Star, User } from "lucide-react";
import { useUserFeedbackResponses } from "@/hooks/api-hooks";
import { useAuth } from "@/contexts/auth-context";
import type { FeedbackResponseRecord } from "@/types";

type FeedbackHistoryItem = {
  id: string
  title: string
  content: string
  category: string
  rating?: number
  author: {
    name: string
    avatar: string | null
  }
  createdAt: Date
  feedbackType: 'targeted' | 'regular'
  isAnonymous: boolean
};

export function FeedbackHistoryPage() {
  const { user } = useAuth();
  const { data: feedbackResponsesData, isLoading } = useUserFeedbackResponses();

  const feedbackResponses: FeedbackResponseRecord[] = useMemo(() => {
    if (Array.isArray(feedbackResponsesData?.data)) {
      return feedbackResponsesData.data as FeedbackResponseRecord[];
    }
    return [];
  }, [feedbackResponsesData]);

  // Filter and format feedback for history display
  const feedbackHistory: FeedbackHistoryItem[] = feedbackResponses
    .filter((response) =>
      (response.target?.id === user?.id || response.targetId === user?.id) &&
      response.submittedAt
    )
    .map((response) => {
      const respondentName = `${response.respondent?.firstName || ''} ${response.respondent?.lastName || ''}`.trim();
      const ratingValue = response.responseRating;
      const normalizedRating = typeof ratingValue === 'number'
        ? ratingValue
        : ratingValue !== null && ratingValue !== undefined
          ? Number(ratingValue)
          : undefined;

      const feedbackType: 'targeted' | 'regular' = response.cycle?.cycleType === 'targeted' ? 'targeted' : 'regular';

      return {
        id: response.id,
        title: response.cycle?.name || "Feedback",
        content: response.responseText || "No text response provided",
        category: response.question?.text?.split(' ')[0] || "General",
        rating: Number.isFinite(normalizedRating) ? normalizedRating : undefined,
        author: {
          name: response.isAnonymous ? "Anonymous" : respondentName || "Unknown",
          avatar: response.respondent?.avatar || null,
        },
        createdAt: new Date(response.submittedAt ?? response.createdAt ?? Date.now()),
        feedbackType,
        isAnonymous: response.isAnonymous || false,
      };
    })
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading feedback history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/feedback"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Feedback
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl flex items-center justify-center">
              <History className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Feedback History</h1>
              <p className="text-muted-foreground text-lg">
                All feedback you've received over time
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Badge className="bg-green-100 text-green-800 border-green-200 px-3 py-1 text-sm font-medium">
              {feedbackHistory.length} Total Feedback
            </Badge>
          </div>
        </div>

        {/* Empty State */}
        {feedbackHistory.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-muted/30 to-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No feedback history yet</h3>
              <p className="text-muted-foreground mb-6">
                Once you receive feedback, it will appear here for you to review.
              </p>
              <Link to="/feedback">
                <Button>
                  Back to Feedback Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Feedback History List */}
        {feedbackHistory.length > 0 && (
          <div className="space-y-6">
            {feedbackHistory.map((item) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        item.feedbackType === 'targeted'
                          ? 'bg-gradient-to-br from-purple-50 to-pink-50'
                          : 'bg-gradient-to-br from-indigo-50 to-purple-50'
                      }`}>
                        {item.feedbackType === 'targeted' ? (
                          <Star className="h-6 w-6 text-purple-600" />
                        ) : (
                          <User className="h-6 w-6 text-indigo-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <div className="font-bold text-foreground text-lg">{item.title}</div>
                          <Badge
                            variant="outline"
                            className={`rounded-lg px-3 py-1 border ${
                              item.feedbackType === 'targeted'
                                ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-purple-200'
                                : 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200'
                            }`}
                          >
                            {item.category}
                          </Badge>
                        </div>
                        <div className="text-muted-foreground">
                          From {item.author.name} • {item.createdAt.toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    {item.rating && (
                      <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1 rounded-lg border border-yellow-200">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-bold text-yellow-700">{item.rating}</span>
                      </div>
                    )}
                  </div>
                  <div className={`p-4 rounded-xl ${
                    item.feedbackType === 'targeted' ? 'bg-muted/50 border border-border' : 'bg-muted/30'
                  }`}>
                    <p className="text-muted-foreground leading-relaxed">
                      "{item.content}"
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FeedbackHistoryPage
